import { useRef, useState } from 'react';
import { duplicateScholarship } from '../../../../services/scholarships';
import { SCHOLARSHIP_CLASSES } from '../../../../types/scholarships';
import type { Scholarship } from '../../../../types/scholarships';
import { ConfirmModal } from '../../../../components/ConfirmModal';
import { getSafeErrorMessage } from '../../../../utils/errorHandler';

interface DuplicateTarget {
  id: string;
  name: string;
}

interface DuplicateScholarshipModalProps {
  scholarship: DuplicateTarget;
  onClose: () => void;
  onSuccess: (copy: Scholarship) => void;
}

// Derived from the ladder itself so the copy can never drift from the behaviour.
// Players on the last class come off the copy instead of advancing, which the sentence
// below spells out separately.
const CLASS_LADDER = SCHOLARSHIP_CLASSES.join(' → ');
const TERMINAL_CLASS = SCHOLARSHIP_CLASSES[SCHOLARSHIP_CLASSES.length - 1];
const HINT_ID = 'duplicate-scholarship-advance-hint';

export const DuplicateScholarshipModal = ({
  scholarship,
  onClose,
  onSuccess,
}: DuplicateScholarshipModalProps) => {
  const [advanceClass, setAdvanceClass] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ConfirmModal deliberately keeps Cancel/Escape live while busy, so a request
  // that never settles can't trap the user. Duplicating is NOT idempotent, so if
  // they back out mid-flight we must not yank them into a copy they just tried
  // to cancel — the request still completes server-side, we simply don't follow it.
  const dismissedRef = useRef(false);

  const handleClose = () => {
    dismissedRef.current = true;
    onClose();
  };

  const handleConfirm = async () => {
    if (isDuplicating) return;
    setIsDuplicating(true);
    setError(null);

    let copy: Scholarship;
    try {
      copy = await duplicateScholarship(scholarship.id, advanceClass);
    } catch (e) {
      setError(getSafeErrorMessage(e, 'Failed to duplicate roster'));
      setIsDuplicating(false);
      return;
    }

    // Deliberately outside the try: the copy already exists, so a throw from
    // onSuccess must not surface as "duplication failed" and invite a retry
    // that would create a second copy.
    if (dismissedRef.current) return;
    onSuccess(copy);
  };

  return (
    <ConfirmModal
      isOpen
      onClose={handleClose}
      onConfirm={handleConfirm}
      title="Duplicate roster"
      message={`This creates a copy of '${scholarship.name}' with all of its players. The original roster is not changed.`}
      confirmText={isDuplicating ? 'Duplicating...' : 'Duplicate'}
      busy={isDuplicating}
    >
      <label className="scholarship-modal__toggle">
        <input
          type="checkbox"
          data-testid="advance-class-checkbox"
          aria-describedby={HINT_ID}
          checked={advanceClass}
          onChange={(e) => setAdvanceClass(e.target.checked)}
          disabled={isDuplicating}
        />
        <span>Advance everyone by a year</span>
      </label>
      <p id={HINT_ID} className="scholarship-modal__hint">
        Moves every player in the copy up one class ({CLASS_LADDER}). Players at{' '}
        {TERMINAL_CLASS} are out of eligibility, so they are left off the copy. Players
        with a class year, or with no class set, are left unchanged.
      </p>
      {error && (
        <div className="scholarship-modal__error" role="alert">{error}</div>
      )}
    </ConfirmModal>
  );
};
